import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Link } from 'react-router-dom';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import { getItem } from '../../../../utils/localStorage';

const ClosedRFQsTab = () => {
  // State for data
  const [loading, setLoading] = useState(false);
  const [closedRfqs, setClosedRfqs] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // Fetch current user data when component mounts
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        let parsedUser;
        
        // Check if the value needs to be parsed
        if (typeof currentUserValue === "string") {
          try {
            parsedUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
          }
        } else {
          parsedUser = currentUserValue; // Assume it's already an object
        }
        
        // Safely access and set user details
        if (parsedUser?.user) {
          setCurrentUser(parsedUser.user);
        } else {
          console.error("Invalid user structure:", parsedUser);
        }
      } catch (err) {
        console.log(err.message);
      }
    };
    
    getCurrentUser();
  }, []);

  // Fetch supplier RFQs when user data is available
  useEffect(() => {
    if (currentUser && currentUser.userId) {
      fetchClosedRfqs(currentUser.userId);
    }
  }, [currentUser]);

  // Function to fetch only closed RFQs for the current supplier
  const fetchClosedRfqs = async (supplierId) => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/app/quotations/suppliers/${supplierId}`,
        "GET"
      );
  
      if (response.success && response.data) {
        // Check if data is an array directly or if it's within response.data.data
        const rfqData = Array.isArray(response.data) ? response.data : response.data.data;
        
        if (Array.isArray(rfqData) && rfqData.length > 0) {
          // Filter only closed RFQs
          const filteredClosedRfqs = rfqData.filter(rfq => rfq.status === 'Closed');
          setClosedRfqs(filteredClosedRfqs);
          
          if (filteredClosedRfqs.length === 0) {
            toastify("No closed RFQs found", "info");
          }
        } else {
          setClosedRfqs([]);
          toastify("No RFQs found in the data", "info");
        }
      } else {
        setClosedRfqs([]);
        toastify("No RFQs found or error fetching data", "info");
      }
    } catch (error) {
      toastify(error.message || "Error fetching closed RFQs", "error");
    } finally {
      setLoading(false);
    }
  };

  const statusTemplate = (rowData) => {
    const statusStyles = {
      Pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
      Active: { color: '#28a745', backgroundColor: '#e9fbe9' },
      Closed: { color: '#6c757d', backgroundColor: '#f1f1f1' }
    };
    
    const style = statusStyles[rowData.status] || {
      color: '#6c757d',
      backgroundColor: '#f1f1f1'
    };
    
    return (
      <span
        style={{
          ...style,
          padding: '0.5em 1em',
          borderRadius: '12px',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          display: 'inline-block'
        }}
      >
        {rowData.status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openingDateTemplate = (rowData) => {
    return formatDate(rowData.startDate);
  };

  const closingDateTemplate = (rowData) => {
    return formatDate(rowData.closingDate);
  };

  const rfqFeeTemplate = (rowData) => {
    if (rowData.rfqFee === 0 || rowData.rfqFee === undefined) {
      return 'Free';
    }
    
    return `${rowData.currency || ''} ${rowData.rfqFee?.toFixed(2) || 0}`;
  };

  const categoryTemplate = (rowData) => {
    return rowData.category || rowData.rfqType || 'N/A';
  };
  
  const actionTemplate = (rowData) => {
    return (
      <Link
        to={`/supplier/rfq_management/view_rfq/${rowData._id}`}
        style={{
          color: '#007bff',
          textDecoration: 'none',
          fontSize: '18px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
        title="View RFQ"
      >
        <i className="ti ti-eye"></i>
      </Link>
    );
  };

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="mb-0">Closed RFQs</h4>
      </div>
      <div className="card-body">
        <DataTable
          value={closedRfqs}
          emptyMessage="No closed RFQs found."
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          stripedRows
          loading={loading}
          tableStyle={{ minWidth: '100%' }}
          responsiveLayout="scroll"
          sortMode="multiple"
        >
          <Column field="rfqNumber" header="RFQ Number" sortable />
          <Column field="name" header="RFQ Name" sortable />
          <Column header="Category" body={categoryTemplate} sortable field="category" />
          <Column header="RFQ Fee" body={rfqFeeTemplate} sortable field="rfqFee" />
          <Column header="Opening Date" body={openingDateTemplate} sortable field="startDate" />
          <Column header="Closing Date" body={closingDateTemplate} sortable field="closingDate" />
          <Column field="status" header="Status" body={statusTemplate} sortable />
          <Column header="Actions" body={actionTemplate} style={{ width: '100px' }} />
        </DataTable>
      </div>
    </div>
  );
};

export default ClosedRFQsTab;